/* eslint-disable @babel/no-unused-expressions */
import { Controller } from '@hotwired/stimulus';

export const create = () => {
	return class extends Controller {
		static values = {
			triggerClass: { type: Boolean, default: true },
		};

		connect() {
			this.handleEscape = this.handleEscape.bind(this);
			this.handleOutsideClick = this.handleOutsideClick.bind(this);
			document.addEventListener('keydown', this.handleEscape);
			document.addEventListener('pointerdown', this.handleOutsideClick);
		}

		disconnect() {
			document.removeEventListener('keydown', this.handleEscape);
			document.removeEventListener('pointerdown', this.handleOutsideClick);
		}

		handleOutsideClick = (e) => {
			if (!this.triggerClassValue) return;
			if (this.element.contains(e.target)) return;

			const classToRemove = this.element.getAttribute('data-toggle-class') ?? 'is-open';
			const closest = this.element.getAttribute('data-toggle-closest');
			const content = this.element.getAttribute('data-toggle-content');

			let targetElements = [];

			if (closest) {
				const targetEl = this.element.closest(closest);
				if (targetEl && targetEl.classList.contains(classToRemove)) {
					targetElements.push(targetEl);
				}
			} else if (content) {
				document.querySelectorAll(content).forEach((el) => {
					if (el.classList.contains(classToRemove)) {
						targetElements.push(el);
					}
				});
			}

			targetElements.forEach((el) => {
				el.classList.remove(classToRemove);
			});

			if (content && targetElements.length > 0) {
				this.updateMatchingTriggers(content, false, classToRemove);
			}
		};

		handleEscape = (e) => {
			if (e.key === 'Escape') {
				const classToRemove = this.element.getAttribute('data-toggle-class') ?? 'is-open';
				const closest = this.element.getAttribute('data-toggle-closest');
				const content = this.element.getAttribute('data-toggle-content');

				let targetElements = [];
				const activeEl = document.activeElement;

				if (closest) {
					const targetEl = this.element.closest(closest);
					if (targetEl && targetEl.classList.contains(classToRemove)) {
						targetElements.push(targetEl);
					}
				} else if (content) {
					document.querySelectorAll(content).forEach((el) => {
						if (el.classList.contains(classToRemove)) {
							targetElements.push(el);
						}
					});
				} else if (this.element.classList.contains(classToRemove)) {
					targetElements.push(this.element);
				}

				// Remove class from target elements
				targetElements.forEach((el) => {
					el.classList.remove(classToRemove);
				});

				// Find and update triggers that might have opened these elements
				if (targetElements.length > 0) {
					document
						.querySelectorAll(`[data-toggle-class="${classToRemove}"], [data-toggle-closest], [data-toggle-content]`)
						.forEach((trigger) => {
							if (this.triggerClassValue && trigger.classList.contains(classToRemove)) {
								trigger.classList.remove(classToRemove);
							}
							if (trigger.hasAttribute('aria-expanded')) {
								trigger.setAttribute('aria-expanded', 'false');
							}
						});

					// Also update the trigger for this controller instance (e.g. footer button uses only data-action)
					const instanceTrigger =
						this.trigger && this.trigger.isConnected
							? this.trigger
							: this.element.querySelector('[data-action~="toggle-class#toggle"][aria-expanded]');

					if (instanceTrigger) {
						instanceTrigger.setAttribute('aria-expanded', 'false');

						const focusWasInsideClosedElement =
							activeEl && targetElements.some((el) => el === activeEl || el.contains(activeEl));

						if (focusWasInsideClosedElement) {
							instanceTrigger.focus();
						}
					}
				}
			}
		};

		toggle = (e) => {
			this.trigger = e.currentTarget;

			if (!(this.trigger?.tagName === 'BUTTON' && ((this.trigger.getAttribute('type') || 'submit').toLowerCase() === 'submit'))) {
				e.preventDefault();
			}
			this.class = this.trigger.getAttribute('data-toggle-class') ?? 'is-open';
			this.closest = this.trigger.getAttribute('data-toggle-closest');
			this.content = this.trigger.getAttribute('data-toggle-content');

			if (this.closest) {
				// Nejbližší element
				this.toggleEl(this.trigger.closest(this.closest));
				this.toggleTrigger();
			} else if (this.content) {
				// Vlastní selektor
				if (this.trigger.closest('[data-controller="toggle-class"]') != this.trigger) {
					this.element.querySelectorAll(this.content).forEach((el) => this.toggleEl(el));
				} else {
					document.querySelectorAll(this.content).forEach((el) => this.toggleEl(el));
				}
				this.toggleTrigger();
			} else if (this.element != this.trigger) {
				// Element, na kterém je controler
				this.toggleEl(this.element);
				this.toggleTrigger();
			}

			const href = this.trigger.getAttribute('href');
			if (href && href.startsWith('#')) {
				const targetId = href.substring(1);
				const anchorElement = document.getElementById(targetId);
				if (anchorElement) {
					anchorElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			}
		};

		toggleEl = (el) => {
			el.classList.toggle(this.class);
		};

		toggleTrigger = () => {
			let targetElement = null;
			if (this.closest) targetElement = this.trigger.closest(this.closest);
			else if (this.content) {
				const scope =
					this.trigger.closest('[data-controller="toggle-class"]') != this.trigger ? this.element : document;
				targetElement = scope.querySelector(this.content);
			} else if (this.element != this.trigger) targetElement = this.element;

			const isOpen = targetElement?.classList.contains(this.class) ?? false;
			if (this.content) {
				this.updateMatchingTriggers(this.content, isOpen, this.class);
				return;
			}

			if (this.triggerClassValue) this.trigger.classList[isOpen ? 'add' : 'remove'](this.class);
			if (this.trigger.hasAttribute('aria-expanded')) {
				this.trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
			}
		};

		updateMatchingTriggers = (contentSelector, isOpen, className) => {
			document.querySelectorAll(`[data-toggle-content="${contentSelector}"]`).forEach((trigger) => {
				if (this.triggerClassValue && trigger.classList.contains(className) !== isOpen) {
					trigger.classList[isOpen ? 'add' : 'remove'](className);
				}
				if (trigger.hasAttribute('aria-expanded')) {
					trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
				}
			});
		};

		toggleInp = (e) => {
			this.trigger = e.currentTarget;
			this.class = this.trigger.getAttribute('data-toggle-class') ?? 'is-open';
			this.closest = this.trigger.getAttribute('data-toggle-closest');
			this.content = this.trigger.getAttribute('data-toggle-content');

			if (this.closest) {
				this.trigger.closest(this.closest).classList[this.trigger.checked ? 'add' : 'remove'](this.class);
			} else if (this.content) {
				document.querySelectorAll(this.content).forEach((el) => this.toggleEl(el));
			} else {
				this.element.classList[this.trigger.checked ? 'add' : 'remove'](this.class);
			}
		};
	};
};
